import React, {createContext, useContext, useEffect} from "react";
import {useAccount, useMsal} from "@azure/msal-react";
import {BrowserAuthError, BrowserAuthErrorCodes, InteractionRequiredAuthError} from "@azure/msal-browser";
import type {AccountInfo, AuthenticationResult} from "@azure/msal-browser";
import {defaultScopes} from "./msalConfig";
import {setAuthenticatedUser} from "../telemetry";
import type {AuthContext} from "../types";

const AuthCtx = createContext<AuthContext | null>(null);

export function AuthContextProvider({ children }: { children: React.ReactNode }) {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] ?? null) as AccountInfo | null;

  useEffect(() => {
    if (account) setAuthenticatedUser(account.username, account.localAccountId);
  }, [account?.localAccountId]);

  if (!account) return null;

  // Falls back to a full-page redirect when silent renewal can't succeed (expired session,
  // consent/MFA needed, or the hidden iframe timing out) instead of surfacing the error to every
  // remote. The original error is still rethrown so callers settle while the page navigates away.
  const acquireSilent = async (scopes: string[]): Promise<AuthenticationResult> => {
    try {
      return await instance.acquireTokenSilent({ scopes, account });
    } catch (err) {
      const needsInteraction =
        err instanceof InteractionRequiredAuthError ||
        (err instanceof BrowserAuthError && err.errorCode === BrowserAuthErrorCodes.timedOut);
      if (needsInteraction) {
        // A concurrent caller may already have started the redirect (interaction_in_progress).
        instance.acquireTokenRedirect({ scopes, account }).catch(() => undefined);
      }
      throw err;
    }
  };

  const acquireToken = async (scopes = defaultScopes): Promise<string> => {
    const {accessToken} = await acquireSilent(scopes);
    return accessToken;
  };

  const acquireTokens = async (scopes = defaultScopes): Promise<{accessToken: string, idToken: string}> => {
    const {accessToken, idToken} = await acquireSilent(scopes);
    return {accessToken, idToken};
  };

  const signOut = () => instance.logoutRedirect({ account });

  const value: AuthContext = {
    user: {
      name: account.name,
      username: account.username,
      localAccountId: account.localAccountId,
    },
    acquireToken,
    acquireTokens,
    signOut,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContext {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthContextProvider");
  return ctx;
}
